import { Component, OnInit, OnDestroy, LOCALE_ID, Inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { EMPTY, interval, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil, catchError } from 'rxjs/operators';
import { OrderService } from '../../../../services/order.service';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { InspectionFooterComponent } from '../inspection-footer/inspection-footer.component';
import { OrderData, OrderState } from '../../../../shared/models/order';
import { Utility } from '../../../../services/utility/util';
import { BackendError, ErrorMessages, ErrorService } from '../../../../services/error-service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Language } from '../../../../shared/models/language';
import { OrderDetailsComponent } from "../order-details/order-details.component";
import { ErrorPageComponent } from '../../../error-page/error-page.component';
import { DANISH_TIMEZONE, TimeService } from '../../../../services/time.service';
import { DanishTimePipe } from "../../../../services/utility/danish-time.pipe";

interface Passengers {
    type: string;
    count: number;
}

@Component({
    selector: 'app-order-content',
    templateUrl: './main-order-page.component.html',
    styleUrls: ['./main-order-page.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        HeaderComponent,
        InspectionFooterComponent,
        ErrorPageComponent,
        TranslateModule,
        OrderDetailsComponent,
        DanishTimePipe
    ],
    providers: [DatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderPageComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    orderData$!: Observable<OrderData>;
    localeKey$!: Observable<string>;

    currentLocale: string;
    currentTime: Date;
    passengers: Passengers[] = [];
    error: (ErrorMessages & { operationId?: string }) | null = null;

    showOrderDetails: boolean = false;

    constructor(
        public utility: Utility,
        private route: ActivatedRoute,
        private orderService: OrderService,
        private datePipe: DatePipe,
        private errorService: ErrorService,
        private translate: TranslateService,
        private cdr: ChangeDetectorRef,
        private timeService: TimeService,
        @Inject(LOCALE_ID) private locale: string
    ) {
        this.currentLocale = this.locale;
        this.currentTime = this.timeService.now();
    }

    ngOnInit(): void {
        this.initializeLanguageTracking();
        this.initializeOrderData();
        this.initializeTimer();
    }

    private initializeLanguageTracking(): void {
        this.localeKey$ = this.utility.getLocaleKey().pipe(
            map(locale => {
                this.currentLocale = locale;
                return locale;
            }),
            takeUntil(this.destroy$)
        );
    }

    private initializeOrderData(): void {
        this.orderData$ = this.route.paramMap.pipe(
            map(params => params.get('id')),
            filter((id): id is string => !!id && id.trim().length > 0),
            switchMap(id => this.orderService.getOrder(id).pipe(
                catchError((err: BackendError) => {
                    this.handleOrderError(err);
                    return EMPTY;
                })
            )),
            map(orderData => this.processOrderData(orderData)),
            takeUntil(this.destroy$)
        );
    }

    private processOrderData(orderData: OrderData): OrderData {
        try {
            this.passengers = orderData.passengers.map(passenger => ({
                type: 'web.' + passenger.type.toLowerCase(),
                count: passenger.count
            }));
            return orderData;
        } catch (error) {
            console.error('Error processing order data:', error);
            throw error;
        }
    }

    private handleOrderError(err: BackendError): void {
        const messages = this.errorService.getErrorMessages(err.Id);
        this.error = { ...messages, operationId: err.operationId };
    }

    private initializeTimer(): void {
        interval(1000)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.currentTime = this.timeService.now();
                    this.cdr.markForCheck();
                },
                error: (error) => {
                    console.error('Timer subscription error:', error);
                }
            });
    }

    getDisplayPassengerType(passenger: Passengers, orderData: OrderData): string {
        const customLabel = this.utility.getCustomPassengerLabel(
            passenger.type,
            orderData.specialTicket?.overriddenPassengerTypes,
            this.currentLocale
        );
        return customLabel || passenger.type;
    }

    getTicketValidityClass(orderData: OrderData): string {
        const now = this.currentTime;

        if (!this.isTicketPaid(orderData)) return 'not-valid';
        if (now < orderData.validFrom) return 'not-valid-yet';
        if (now > orderData.validTo) return 'expired'
        return '';
    }

    getTicketTimeLeft(orderData: OrderData): string {
        const now = this.currentTime;

        if (!this.isTicketPaid(orderData)) {
            return this.translate.instant('web.ticketInvalid');
        }

        if (now > orderData.validTo) {
            return this.translate.instant('web.ticketExpired');
        }

        if (now < orderData.validFrom) {
            const diffMs = orderData.validFrom.getTime() - now.getTime();

            const totalSeconds = Math.floor(diffMs / 1000);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;

            const hourUnit = this.translate.getCurrentLang() === Language.Danish ? 't' : 'h';

            if (hours > 0) {
                return `${this.translate.instant('web.ticketValidIn')} ${hours}${hourUnit} ${minutes}min ${seconds}s`;
            } else if (minutes > 0) {
                return `${this.translate.instant('web.ticketValidIn')} ${minutes}min ${seconds}s`;
            } else {
                return `${this.translate.instant('web.ticketValidIn')} ${seconds}s`;
            }
        }

        return '';
    }

    isTicketPaid(orderData: OrderData): boolean {
        return orderData.status === OrderState.Paid;
    }

    get clockText(): string {
        const danishTime = this.timeService.toDanishTime(this.currentTime);
        return this.datePipe.transform(
            danishTime, 'HH:mm:ss', DANISH_TIMEZONE
        ) ?? '';
    }

    get dayText(): string {
        return this.datePipe.transform(
            this.currentTime, 'EEEE', DANISH_TIMEZONE, this.currentLocale
        ) ?? '';
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
